/**
 * Ключевая ставка Банка России.
 * Снимок config/reference/key_rate_cbr.yaml на 2026-10-07.
 * Таблица зашита: лендинг считает без запроса к API.
 */
import { addDaysIso, compareIso, formatRuDate, parseIsoDate } from "@/lib/legal/calendar"

export const KEY_RATE_SOURCE = "Банк России"
export const KEY_RATE_SOURCE_URL = "https://www.cbr.ru/hd_base/KeyRate/"
export const KEY_RATE_UPDATED_AT = "2026-10-07"
/** С этой даты проценты по ст. 395 ГК считаются по ключевой ставке. */
export const ART395_KEY_RATE_FROM = "2016-08-01"

export type KeyRate = {
  from: string
  percent: number
}

export const KEY_RATES: readonly KeyRate[] = [
  { from: "2016-06-14", percent: 10.5 },
  { from: "2016-09-19", percent: 10 },
  { from: "2017-03-27", percent: 9.75 },
  { from: "2017-05-02", percent: 9.25 },
  { from: "2017-06-19", percent: 9 },
  { from: "2017-09-18", percent: 8.5 },
  { from: "2017-10-30", percent: 8.25 },
  { from: "2017-12-18", percent: 7.75 },
  { from: "2018-02-12", percent: 7.5 },
  { from: "2018-03-26", percent: 7.25 },
  { from: "2018-09-17", percent: 7.5 },
  { from: "2018-12-17", percent: 7.75 },
  { from: "2019-06-17", percent: 7.5 },
  { from: "2019-07-29", percent: 7.25 },
  { from: "2019-09-09", percent: 7 },
  { from: "2019-10-28", percent: 6.5 },
  { from: "2019-12-16", percent: 6.25 },
  { from: "2020-02-10", percent: 6 },
  { from: "2020-04-27", percent: 5.5 },
  { from: "2020-06-22", percent: 4.5 },
  { from: "2020-07-27", percent: 4.25 },
  { from: "2021-03-22", percent: 4.5 },
  { from: "2021-04-26", percent: 5 },
  { from: "2021-06-15", percent: 5.5 },
  { from: "2021-07-26", percent: 6.5 },
  { from: "2021-09-13", percent: 6.75 },
  { from: "2021-10-25", percent: 7.5 },
  { from: "2021-12-20", percent: 8.5 },
  { from: "2022-02-14", percent: 9.5 },
  { from: "2022-02-28", percent: 20 },
  { from: "2022-04-11", percent: 17 },
  { from: "2022-05-04", percent: 14 },
  { from: "2022-05-27", percent: 11 },
  { from: "2022-06-14", percent: 9.5 },
  { from: "2022-07-25", percent: 8 },
  { from: "2022-09-19", percent: 7.5 },
  { from: "2023-07-24", percent: 8.5 },
  { from: "2023-08-15", percent: 12 },
  { from: "2023-09-18", percent: 13 },
  { from: "2023-10-30", percent: 15 },
  { from: "2023-12-18", percent: 16 },
  { from: "2024-07-29", percent: 18 },
  { from: "2024-09-16", percent: 19 },
  { from: "2024-10-28", percent: 21 },
  { from: "2025-06-09", percent: 20 },
  { from: "2025-07-28", percent: 18 },
  { from: "2025-09-15", percent: 17 },
  { from: "2025-10-27", percent: 16.5 },
  { from: "2025-12-22", percent: 16 },
  { from: "2026-02-16", percent: 15.5 },
  { from: "2026-03-23", percent: 15 },
  { from: "2026-04-27", percent: 14.5 },
  { from: "2026-06-22", percent: 14.25 },
  { from: "2026-07-27", percent: 14 },
]

export type KeyRateSegment = {
  from: string
  to: string
  percent: number
}

export function rateOn(day: string): number {
  parseIsoDate(day)
  let rate: number | null = null
  for (const item of KEY_RATES) {
    if (compareIso(item.from, day) > 0) break
    rate = item.percent
  }
  if (rate === null) {
    throw new Error(`Нет ключевой ставки на дату ${formatRuDate(day)}`)
  }
  return rate
}

/** Отрезки [from, to] включительно с постоянной ставкой. */
export function keyRateSegments(from: string, to: string): KeyRateSegment[] {
  parseIsoDate(from)
  parseIsoDate(to)
  if (compareIso(from, to) > 0) {
    throw new Error("Дата начала просрочки позже даты окончания")
  }

  const segments: KeyRateSegment[] = []
  let cursor = from
  while (compareIso(cursor, to) <= 0) {
    const percent = rateOn(cursor)
    let end = to
    for (const item of KEY_RATES) {
      if (compareIso(item.from, cursor) > 0) {
        const dayBefore = addDaysIso(item.from, -1)
        end = compareIso(dayBefore, to) < 0 ? dayBefore : to
        break
      }
    }
    segments.push({ from: cursor, to: end, percent })
    const next = addDaysIso(end, 1)
    if (compareIso(next, cursor) <= 0) {
      throw new Error("Не удалось разбить период по ставкам")
    }
    cursor = next
  }
  return segments
}
